import { useEffect, useRef, useState } from "react";
import { Bell, Check, CheckCheck } from "lucide-react";
import { api, formatDate } from "../lib/api";

const POLL_MS = 20000;
const seenIdsKey = "dbs_field_seen_notifications";

function getSeenIds() {
  try { return new Set(JSON.parse(localStorage.getItem(seenIdsKey) || "[]")); }
  catch (e) { console.warn("notif: failed to read seen ids", e); return new Set(); }
}
function persistSeen(set) {
  try { localStorage.setItem(seenIdsKey, JSON.stringify(Array.from(set).slice(-500))); }
  catch (e) { console.warn("notif: failed to persist seen ids", e); }
}

async function notifyBrowser(title, body) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    // Prefer service-worker registration when installed as PWA — allows click actions
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        reg.showNotification(title, { body, icon: "/dbs-logo.jpeg", badge: "/dbs-logo.jpeg", tag: "dbs-field-notif" });
        return;
      }
    }
    new Notification(title, { body, icon: "/dbs-logo.jpeg" });
  } catch (e) {
    console.warn("notif: showNotification failed", e);
  }
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");
  const seenRef = useRef(getSeenIds());
  const wrapRef = useRef(null);

  const fetchNotifs = async () => {
    try {
      const res = await api.get("/notifications?limit=20");
      const list = res.data?.items || [];
      setItems(list);
      setUnread(res.data?.unread_count || 0);
      // Fire OS notifications for previously-unseen unread items
      const seen = seenRef.current;
      for (const n of list) {
        if (!n.read && !seen.has(n.id)) {
          notifyBrowser(n.title, n.body);
          seen.add(n.id);
        }
      }
      persistSeen(seen);
    } catch (e) {
      // Silent for polling: transient network errors are expected and would spam the UI.
      // Log at debug level so it's still visible during troubleshooting.
      if (e?.response?.status && e.response.status >= 500) {
        console.warn("notif: fetch failed", e);
      }
    }
  };

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, POLL_MS);
    const onFocus = () => fetchNotifs();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const requestPermission = async () => {
    if (typeof Notification === "undefined") return;
    const p = await Notification.requestPermission();
    setPermission(p);
  };

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); fetchNotifs(); }
    catch (e) { console.warn("notif: mark-read failed", e); }
  };
  const markAll = async () => {
    try { await api.post("/notifications/read-all"); fetchNotifs(); }
    catch (e) { console.warn("notif: mark-all failed", e); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        className="btn btn-ghost relative"
        onClick={() => setOpen((v) => !v)}
        data-testid="notif-bell"
        title="Notificações"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-1 -right-1 text-[10px] font-bold px-1.5 py-0.5"
            style={{ background: "var(--primary)", color: "#fff", borderRadius: 999, minWidth: 18, textAlign: "center" }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-2 z-50 shadow-2xl"
          style={{ width: 360, background: "var(--surface)", border: "1px solid var(--border-strong)" }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
            <div className="label-caps">Notificações</div>
            <div className="flex items-center gap-2">
              {permission !== "granted" && (
                <button className="btn btn-ghost text-xs" onClick={requestPermission} data-testid="notif-permission">
                  Ativar avisos
                </button>
              )}
              {unread > 0 && (
                <button className="btn btn-ghost text-xs" onClick={markAll} data-testid="notif-mark-all">
                  <CheckCheck size={12} /> Marcar todas
                </button>
              )}
            </div>
          </div>
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {items.length === 0 && (
              <div className="p-6 text-center text-sm" style={{ color: "var(--text-mute)" }}>
                Nenhuma notificação.
              </div>
            )}
            {items.map((n) => (
              <div
                key={n.id}
                className="px-4 py-3 border-b flex items-start gap-3"
                style={{
                  borderColor: "var(--border)",
                  background: n.read ? "transparent" : "rgba(56,189,248,0.06)",
                }}
                data-testid={`notif-${n.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{n.title}</div>
                  {n.body && <div className="text-xs mt-0.5" style={{ color: "var(--text-dim)" }}>{n.body}</div>}
                  <div className="text-[10px] mt-1" style={{ color: "var(--text-mute)" }}>{formatDate(n.created_at)}</div>
                </div>
                {!n.read && (
                  <button className="btn btn-ghost" onClick={() => markRead(n.id)} title="Marcar como lida" data-testid={`notif-read-${n.id}`}>
                    <Check size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
